import type { Collect, CollectContext } from '../types';

export function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function getTimezone(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz && tz !== 'UTC') return tz;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
}

export function getLocalISOStringWithOffset(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const min = pad(date.getMinutes());
  const sec = pad(date.getSeconds());
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const offsetHour = pad(Math.floor(absOffset / 60));
  const offsetMin = pad(absOffset % 60);
  return `${year}-${month}-${day}T${hour}:${min}:${sec}.${ms}${sign}${offsetHour}:${offsetMin}`;
}

export function getTimezoneRegionAndOffset(): string {
  const region = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60));
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
  return `${region} (GMT${sign}${hours}${minutes !== '00' ? ':' + minutes : ''})`;
}

export function formatTime(time: string): string {
  return time.split(':').slice(0, 2).join(':');
}

export function parseCollectContext(collect: Collect): CollectContext | undefined {
  let ctx = collect.context;
  if (typeof ctx === 'string') {
    try {
      ctx = JSON.parse(ctx) as CollectContext;
    } catch {
      return undefined;
    }
  }
  return ctx as CollectContext | undefined;
}

export function maskCPF(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9, 11)}`;
}

export function formatCPF(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function resolveCheckValue(collect: Collect): 'in' | 'out' | null {
  let ctx = collect.context;
  if (typeof ctx === 'string') {
    try {
      ctx = JSON.parse(ctx) as CollectContext;
    } catch {
      ctx = undefined;
    }
  }
  const check = (ctx as CollectContext | undefined)?.check;
  if (check === 'in') return 'in';
  if (check === 'out') return 'out';
  const t = collect.type.toLowerCase();
  if (t.includes('entrando') || t === 'in') return 'in';
  if (t.includes('saindo') || t === 'out') return 'out';
  return null;
}
